import { useQuery } from '@tanstack/react-query';
import { api } from './api';
import type { SubscriptionData, SubscriptionSummaryData } from '@/types/api';

const KEY = ['subscriptions'] as const;

/** Human label for a Stripe billing interval (e.g. every 3 months → "Quarterly"). */
export function intervalLabel(interval: string, count = 1): string {
  if (count === 1) {
    return (
      { day: 'Daily', week: 'Weekly', month: 'Monthly', year: 'Yearly' }[interval] ??
      `Every ${interval}`
    );
  }
  return `Every ${count} ${interval}s`;
}

export function useClientSubscriptions(clientId: number | undefined) {
  return useQuery({
    queryKey: [...KEY, 'client', clientId],
    enabled: clientId != null,
    queryFn: async (): Promise<SubscriptionData[]> => {
      const { data } = await api.get<SubscriptionData[]>(
        `/v1/clients/${clientId}/subscriptions`,
      );
      return data;
    },
  });
}

export type SubscriptionSegment = 'active' | 'at_risk' | 'ended' | 'all';

/** Every subscription in a segment. Live segments arrive soonest renewal first. */
export function useAllSubscriptions(segment: SubscriptionSegment = 'all') {
  return useQuery({
    queryKey: [...KEY, 'all', segment],
    queryFn: async (): Promise<SubscriptionData[]> => {
      const { data } = await api.get<SubscriptionData[]>('/v1/subscriptions', { params: { segment } });
      return data;
    },
  });
}

/** MRR, ARR, renewals and what's at risk, plus the short lists the dashboard shows. */
export function useSubscriptionSummary() {
  return useQuery({
    queryKey: [...KEY, 'summary'],
    queryFn: async (): Promise<SubscriptionSummaryData> => {
      const { data } = await api.get<SubscriptionSummaryData>('/v1/subscriptions/summary');
      return data;
    },
    staleTime: 60_000,
  });
}

export const SUBSCRIPTION_STATUS_LABEL: Record<string, string> = {
  active: 'Active',
  trialing: 'Trialing',
  past_due: 'Past due',
  canceled: 'Canceled',
  unpaid: 'Unpaid',
  incomplete: 'Incomplete',
  incomplete_expired: 'Expired',
  paused: 'Paused',
};

export function subscriptionStatusTone(status: string): 'success' | 'warning' | 'neutral' | 'danger' {
  if (status === 'active' || status === 'trialing') return 'success';
  if (status === 'past_due' || status === 'unpaid') return 'danger';
  if (status.startsWith('incomplete') || status === 'paused') return 'warning';
  return 'neutral';
}

/**
 * The status a person reads first. An active subscription set to end shows as
 * ending rather than active, since that's the part worth noticing.
 */
export function subscriptionStatusLabel(sub: SubscriptionData): string {
  if (sub.counts_toward_mrr && sub.cancel_at_period_end) return 'Ending';
  return SUBSCRIPTION_STATUS_LABEL[sub.status] ?? sub.status;
}
